import { useState, useMemo, useCallback } from "react";
import { ChevronDown } from "lucide-react";

export type BusinessUnitMultiSelectProps = {
  readonly id: string;
  readonly options: readonly string[];
  readonly value: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  readonly disabled?: boolean;
};

export const BusinessUnitMultiSelect = ({
  id,
  options,
  value,
  onChange,
  disabled = false
}: BusinessUnitMultiSelectProps) => {
  const [open, setOpen] = useState(false);

  const normalized = useMemo(
    () => options.map((o) => o.trim()).filter((o) => o.length > 0),
    [options]
  );

  const display = useMemo(() => {
    if (value.length === 0) {
      return "Select business units";
    }
    return `${value.length} selected: ${value.join(", ")}`;
  }, [value]);

  const allSelected =
    normalized.length > 0 && value.length === normalized.length && normalized.every((o) => value.includes(o));

  const toggle = useCallback(
    (option: string) => {
      if (value.includes(option)) {
        onChange(value.filter((v) => v !== option));
      } else {
        onChange([...value, option]);
      }
    },
    [onChange, value]
  );

  const selectAll = useCallback(() => {
    onChange([...normalized]);
  }, [normalized, onChange]);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm transition-all duration-200 ease-in-out disabled:opacity-50"
      >
        <span className="truncate text-left">{display}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open && !disabled ? (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-[var(--border)] bg-white py-1 shadow-lg">
          <button
            type="button"
            className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm font-medium text-primary transition-all duration-200 ease-in-out hover:bg-muted/50"
            onClick={selectAll}
          >
            Select all
          </button>
          {normalized.map((option) => {
            const checked = value.includes(option);
            return (
              <label key={option} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(option)}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      ) : null}
      {allSelected ? (
        <p className="mt-1 text-xs text-muted-foreground">All business units selected.</p>
      ) : null}
    </div>
  );
};
